import { c as _c } from "react/compiler-runtime";
import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { useCallback, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedButton } from 'record-it-components/button/FixedButton/FixedButton';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { IconDeprecated, IconType } from 'record-it-components/misc/IconDeprecated/IconDeprecated';
import { SelectModal } from 'record-it-components/modal/SelectModal/SelectModal';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useStopwatchManager } from '../../../hooks/stopwatch/useStopwatchManager';
export type StartNewStopwatchFixedButtonProps = PropsWithoutChildren<{
  'data-testid'?: string;
}>;
export const StartNewStopwatchFixedButton: FunctionComponent<StartNewStopwatchFixedButtonProps> = React.memo(props => {
  const $ = _c(28);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    "data-testid": dataTestid
  } = t0;
  const {
    t
  } = useTranslation();
  const manager = useStopwatchManager();
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      closeOnBackDropClick: true
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    open: openSelectUserGroupModal,
    close: closeSelectUserGroupModal,
    modalProps
  } = useModalOpened(t1);
  const userGroupRepository = useEntityRepository(EntityName.UserGroup);
  let t2;
  if ($[3] !== manager || $[4] !== openSelectUserGroupModal || $[5] !== userGroupRepository) {
    t2 = () => {
      const userGroups = userGroupRepository.getAll();
      if (userGroups.length === 1) {
        const firstUserGroup = userGroups[0];
        assertNotNullOrUndefined(firstUserGroup, "userGroups have a length but not first item? wtf");
        manager.startNew(firstUserGroup.id);
      } else {
        if (userGroups.length > 1) {
          openSelectUserGroupModal();
        } else {
          throw new Error("no usergroup available");
        }
      }
    };
    $[3] = manager;
    $[4] = openSelectUserGroupModal;
    $[5] = userGroupRepository;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleAddStopwatchClick = useEventCallback(t2);
  const userGroups_0 = useEntities(EntityName.UserGroup, "getAll");
  const renderContentOfUserGroup = _temp;
  const getKeyOfUserGroup = _temp2;
  let t3;
  if ($[7] !== closeSelectUserGroupModal || $[8] !== manager) {
    t3 = userGroup_1 => {
      manager.startNew(userGroup_1.id);
      closeSelectUserGroupModal();
    };
    $[7] = closeSelectUserGroupModal;
    $[8] = manager;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleUserGroupSelected = useEventCallback(t3);
  const t4 = dataTestid ?? "StartNewStopwatchFixedButton.Button";
  let t5;
  if ($[10] !== t) {
    t5 = t("components.timeTracking.StartNewStopwatchFixedButton.startNewStopwatch");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconDeprecated name="plus-circle" type={IconType.Light} hasTextOnLeftSide={true} />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== handleAddStopwatchClick || $[14] !== t4 || $[15] !== t5) {
    t7 = <FixedButton onClick={handleAddStopwatchClick} data-testid={t4}>{t5}{t6}</FixedButton>;
    $[13] = handleAddStopwatchClick;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("components.timeTracking.StartNewStopwatchFixedButton.userGroupSelectionTitle");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== closeSelectUserGroupModal || $[20] !== handleUserGroupSelected || $[21] !== modalProps || $[22] !== t8 || $[23] !== userGroups_0) {
    t9 = <SelectModal {...modalProps} label={t8} items={userGroups_0} getKeyOfItem={getKeyOfUserGroup} renderContentOfItem={renderContentOfUserGroup} onCloseButtonClick={closeSelectUserGroupModal} onItemSelected={handleUserGroupSelected} />;
    $[19] = closeSelectUserGroupModal;
    $[20] = handleUserGroupSelected;
    $[21] = modalProps;
    $[22] = t8;
    $[23] = userGroups_0;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t9) {
    t10 = <>{t7}{t9}</>;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
});
function _temp(userGroup) {
  return <span>{userGroup.name}</span>;
}
function _temp2(userGroup_0) {
  return EntityUtils.getTrackingKey(userGroup_0);
}